import React, { useState, useEffect, useCallback } from 'react';
import {
  XMarkIcon,
  UserIcon,
  ClipboardDocumentCheckIcon,
  PlusIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { PlanRectangle, UpdateRectangleDto } from '../../services/planService';
import { usersAPI } from '../../services/api';
import taskService from '../../services/taskService';
import { Task } from '../../types/task.types';
import clsx from 'clsx';

interface User {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
}

interface RectangleDetailModalProps {
  rectangle: PlanRectangle;
  onClose: () => void;
  onUpdate: (data: UpdateRectangleDto) => Promise<void>;
}

const RectangleDetailModal: React.FC<RectangleDetailModalProps> = ({
  rectangle,
  onClose,
  onUpdate,
}) => {
  const [activeTab, setActiveTab] = useState<'details' | 'users' | 'tasks'>('details');
  const [users, setUsers] = useState<User[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [linkedUserIds, setLinkedUserIds] = useState<number[]>(rectangle.linkedUserIds || []);
  const [linkedTaskIds, setLinkedTaskIds] = useState<number[]>(rectangle.linkedTaskIds || []);
  const [userSearch, setUserSearch] = useState('');
  const [taskSearch, setTaskSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load users and tasks
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [usersData, tasksData] = await Promise.all([
          usersAPI.getAll(),
          taskService.getTasks({ limit: 100 }),
        ]);
        setUsers(usersData);
        setTasks(tasksData.data || []);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await onUpdate({ linkedUserIds, linkedTaskIds });
      onClose();
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setSaving(false);
    }
  }, [linkedUserIds, linkedTaskIds, onUpdate, onClose]);

  const toggleUser = (userId: number) => {
    setLinkedUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const toggleTask = (taskId: number) => {
    setLinkedTaskIds(prev =>
      prev.includes(taskId) ? prev.filter(id => id !== taskId) : [...prev, taskId]
    );
  };

  const filteredUsers = users.filter(user => {
    const search = userSearch.toLowerCase();
    return (
      user.email.toLowerCase().includes(search) ||
      user.firstName?.toLowerCase().includes(search) ||
      user.lastName?.toLowerCase().includes(search)
    );
  });

  const filteredTasks = tasks.filter(task => {
    const search = taskSearch.toLowerCase();
    return task.title.toLowerCase().includes(search);
  });

  const linkedUsers = users.filter(u => linkedUserIds.includes(u.id));
  const linkedTasks = tasks.filter(t => linkedTaskIds.includes(t.id));

  const getContrastColor = (hexColor: string): string => {
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#1f2937' : '#ffffff';
  };

  const hasChanges =
    JSON.stringify(linkedUserIds.sort()) !== JSON.stringify((rectangle.linkedUserIds || []).sort()) ||
    JSON.stringify(linkedTaskIds.sort()) !== JSON.stringify((rectangle.linkedTaskIds || []).sort());

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden">
          {/* Header */}
          <div
            className="px-6 py-5 border-b"
            style={{ backgroundColor: rectangle.color + '20' }}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg"
                  style={{ backgroundColor: rectangle.color }}
                >
                  <span
                    className="text-lg font-bold"
                    style={{ color: getContrastColor(rectangle.color) }}
                  >
                    {rectangle.title.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{rectangle.title}</h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {linkedUserIds.length} users • {linkedTaskIds.length} tasks
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <XMarkIcon className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b bg-gray-50">
            {[
              { id: 'details', label: 'Details', icon: null },
              { id: 'users', label: `Users (${linkedUserIds.length})`, icon: UserIcon },
              { id: 'tasks', label: `Tasks (${linkedTaskIds.length})`, icon: ClipboardDocumentCheckIcon },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={clsx(
                  'flex-1 px-4 py-3 text-sm font-medium transition-all flex items-center justify-center gap-2',
                  activeTab === tab.id
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                )}
              >
                {tab.icon && <tab.icon className="h-4 w-4" />}
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="overflow-y-auto" style={{ maxHeight: 'calc(85vh - 200px)' }}>
            {/* Details Tab */}
            {activeTab === 'details' && (
              <div className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                  <div className="text-gray-900 font-medium">{rectangle.title}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <div className="text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-4 min-h-[100px]">
                    {rectangle.content || <span className="text-gray-400 italic">No description</span>}
                  </div>
                </div>

                {/* Linked Users Preview */}
                {linkedUsers.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Linked Users
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {linkedUsers.map(user => (
                        <div
                          key={user.id}
                          className="flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full text-sm"
                        >
                          <UserIcon className="h-4 w-4" />
                          {user.firstName || user.lastName
                            ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                            : user.email}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Linked Tasks Preview */}
                {linkedTasks.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Linked Tasks
                    </label>
                    <div className="space-y-2">
                      {linkedTasks.map(task => (
                        <div
                          key={task.id}
                          className="flex items-center gap-3 bg-green-50 text-green-700 px-4 py-2 rounded-lg"
                        >
                          <ClipboardDocumentCheckIcon className="h-4 w-4 flex-shrink-0" />
                          <span className="text-sm font-medium truncate">{task.title}</span>
                          <span className={clsx(
                            'ml-auto text-xs px-2 py-0.5 rounded-full',
                            task.status === 'COMPLETED' ? 'bg-green-200 text-green-800' :
                            task.status === 'IN_PROGRESS' ? 'bg-blue-200 text-blue-800' :
                            'bg-gray-200 text-gray-700'
                          )}>
                            {task.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="p-6">
                {/* Search */}
                <div className="relative mb-4">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Selected Users */}
                {linkedUserIds.length > 0 && (
                  <div className="mb-4">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                      Selected ({linkedUserIds.length})
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {linkedUsers.map(user => (
                        <button
                          key={user.id}
                          onClick={() => toggleUser(user.id)}
                          className="flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-1.5 rounded-full text-sm hover:bg-blue-200 transition-colors"
                        >
                          <span>{user.firstName || user.email}</span>
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* User List */}
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {loading ? (
                    <div className="text-center py-8 text-gray-500">Loading users...</div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">No users found</div>
                  ) : (
                    filteredUsers.map(user => (
                      <button
                        key={user.id}
                        onClick={() => toggleUser(user.id)}
                        className={clsx(
                          'w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left',
                          linkedUserIds.includes(user.id)
                            ? 'bg-blue-50 border-2 border-blue-300'
                            : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                        )}
                      >
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-medium">
                          {(user.firstName?.[0] || user.email[0]).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 truncate">
                            {user.firstName || user.lastName
                              ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                              : user.email}
                          </div>
                          <div className="text-sm text-gray-500 truncate">{user.email}</div>
                        </div>
                        {linkedUserIds.includes(user.id) && (
                          <CheckIcon className="h-5 w-5 text-blue-600" />
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Tasks Tab */}
            {activeTab === 'tasks' && (
              <div className="p-6">
                {/* Search */}
                <div className="relative mb-4">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search tasks..."
                    value={taskSearch}
                    onChange={(e) => setTaskSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Selected Tasks */}
                {linkedTaskIds.length > 0 && (
                  <div className="mb-4">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                      Selected ({linkedTaskIds.length})
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {linkedTasks.map(task => (
                        <button
                          key={task.id}
                          onClick={() => toggleTask(task.id)}
                          className="flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1.5 rounded-full text-sm hover:bg-green-200 transition-colors"
                        >
                          <span className="truncate max-w-[150px]">{task.title}</span>
                          <XMarkIcon className="h-4 w-4 flex-shrink-0" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Task List */}
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {loading ? (
                    <div className="text-center py-8 text-gray-500">Loading tasks...</div>
                  ) : filteredTasks.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">No tasks found</div>
                  ) : (
                    filteredTasks.map(task => (
                      <button
                        key={task.id}
                        onClick={() => toggleTask(task.id)}
                        className={clsx(
                          'w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left',
                          linkedTaskIds.includes(task.id)
                            ? 'bg-green-50 border-2 border-green-300'
                            : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                        )}
                      >
                        <div className={clsx(
                          'w-10 h-10 rounded-xl flex items-center justify-center',
                          task.status === 'COMPLETED' ? 'bg-green-500' :
                          task.status === 'IN_PROGRESS' ? 'bg-blue-500' :
                          'bg-gray-400'
                        )}>
                          <ClipboardDocumentCheckIcon className="h-5 w-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 truncate">{task.title}</div>
                          <div className="text-sm text-gray-500">
                            {task.priority} • {task.status}
                          </div>
                        </div>
                        {linkedTaskIds.includes(task.id) && (
                          <CheckIcon className="h-5 w-5 text-green-600" />
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              {hasChanges && <span className="text-amber-600">• Unsaved changes</span>}
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className={clsx(
                  'px-5 py-2 rounded-lg font-medium transition-all',
                  hasChanges
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                )}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RectangleDetailModal;

